import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useOnboarding } from '../../../../context/OnboardingContext';
import { PrivateWizardLayout, WizardContainer, WizardCard, WizardActions } from '../../WizardLayout';
import Stepper from '../../Stepper';
import { updateOnboardingSession } from '../../../../services/wizardApi';
import { toast } from 'react-toast';
import { LuGlobe, LuPlay, LuInfo, LuChevronDown } from 'react-icons/lu';
import '../../../../styles/wizard.css';
import { STEP_STATUS } from '../../../../Config/Onboarding/constant';

const Step8BRecordingSetup = () => {
  const navigate = useNavigate();
  const { project, data, recorder, setRecorder, goToStep, previousStep, sessionId, markStepCompleted, setStepStatus } = useOnboarding();
  const [projectUrl, setProjectUrl] = useState(recorder.projectUrl || project.baseUrl || '');
  const [testCaseName, setTestCaseName] = useState(recorder.testCaseName || '');
  const [description, setDescription] = useState(recorder.description || '');
  const [priority, setPriority] = useState(recorder.priority || 'medium');
  const [isStarting, setIsStarting] = useState(false);
  const [errors, setErrors] = useState({});
  const [hasRecorded, setHasRecorded] = useState(recorder?.hasRecorded || false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [isNextStepsOpen, setIsNextStepsOpen] = useState(false);
  const [isRecordingTipsOpen, setIsRecordingTipsOpen] = useState(false);

  // Sync form fields with recorder context when component mounts or context changes
  useEffect(() => {
    setProjectUrl(recorder.projectUrl || project.baseUrl || '');
    setTestCaseName(recorder.testCaseName || '');
    setDescription(recorder.description || '');
    setPriority(recorder.priority || 'medium');
    setHasRecorded(recorder?.hasRecorded || false);
  }, [recorder, project.baseUrl]);

  const validate = () => {
    const newErrors = {};

    if (!testCaseName.trim()) {
      newErrors.testCaseName = 'Test case name is required';
    } else if (testCaseName.trim().length < 3) {
      newErrors.testCaseName = 'Test case name must be at least 3 characters';
    } else if (testCaseName.trim().length > 100) {
      newErrors.testCaseName = 'Test case name must not exceed 100 characters';
    }

    if (description.trim().length > 500) {
      newErrors.description = 'Description must not exceed 500 characters';
    }

    if (!priority) {
      newErrors.priority = 'Priority is required';
    }
    
    if (!projectUrl.trim()) {
      newErrors.projectUrl = 'Project URL is required';
    } else {
      try {
        new URL(projectUrl);
      } catch (e) {
        newErrors.projectUrl = 'Please enter a valid URL (e.g., https://example.com)';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleStartRecording = async () => {
    if (!validate()) return;

    setIsStarting(true);
    try {
      // Send trigger event to browser extension
      window.postMessage(
        {
          source: "ROBITEST_CLOUD",
          action: "TRIGGER_START",
          sessionName: testCaseName.trim(),
          description: description.trim(),
          priority: priority,
          projectId: data.project.project_id || 1,
          projectName: data.project.name || "Default Project",
          environmentId: data.project.environment_id || 1,
          targetUrl: projectUrl,
        },
        "*"
      );

      // Update recorder state with flag
      const recordingSessionId = `session_${Date.now()}`;
      setRecorder({
        ...recorder,
        recordingSessionId: recordingSessionId,
        recordingStatus: 'recording',
        projectUrl: projectUrl,
        testCaseName: testCaseName.trim(),
        description: description.trim(),
        priority: priority,
        hasRecorded: true
      });

      setHasRecorded(true);
      
      // Update session on backend
      if (sessionId) {
        await updateOnboardingSession(sessionId, {
          currentStep: 7,
          recorder: {
            ...recorder,
            recordingSessionId: recordingSessionId,
            projectUrl: projectUrl,
            testCaseName: testCaseName.trim(),
            description: description.trim(),
            priority: priority,
            hasRecorded: true
          }
        });
      }

      toast.success('Recording test case... Chrome will open in a moment');
      
      // Storage indication that user started recording
      setIsStarting(false);
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Failed to start recording. Please try again.');
      setIsStarting(false);
    }
  };

  const handleContinueToStep8 = async () => {
    if (isNavigating) return; // Prevent double-click

    setIsNavigating(true);
    try {
      // Setup is step 7; review is step 8
      markStepCompleted(7);
      setStepStatus(8, STEP_STATUS.IN_PROGRESS);
      
      if (sessionId) {
        await updateOnboardingSession(sessionId, {
          currentStep: 8,
          recorder: {
            ...recorder,
            hasRecorded: true
          }
        });
      }
      
      goToStep(8);
      navigate('/app/onboarding?step=8&mode=recorder');
    } catch (error) {
      console.error('Error proceeding:', error);
      toast.error('Failed to proceed');
      setIsNavigating(false);
    }
    
    // Re-enable button after 2.5 seconds
    setTimeout(() => {
      setIsNavigating(false);
    }, 2500);
  };

  return (
    <PrivateWizardLayout>
      <WizardContainer>
        <Stepper currentStep={7} completedSteps={[1, 2, 3, 4, 5, 6]} />
        
        <WizardCard
          title="Record Test Case"
          subtitle="We'll capture your interactions and generate automation"
        >
          <div style={{ marginBottom: '2rem' }}>
                  {/* Info Box */}
      <div className="mb-8 rounded-lg border border-gray-200">
        {/* Header — isolated, never reflowed */}
        <div className="flex items-center justify-between px-4 py-3 cursor-pointer" onClick={() => setIsNextStepsOpen(!isNextStepsOpen)} >
          <div className="flex items-center gap-3">
            <LuInfo className="text-blue-500 shrink-0" size={20} />
            <span className="font-semibold text-sm text-gray-800">
              What happens next
            </span>
          </div>
          <button
            
            className="p-1"
          >
            <LuChevronDown
              className={`transition-transform duration-200 ${
                isNextStepsOpen ? "rotate-180" : ""
              }`}
              size={20}
            />
          </button>
        </div>

        {/* Animated content — completely decoupled from header */}
        <div
          className="overflow-hidden transition-all duration-300 ease-in-out"
          style={{ maxHeight: isNextStepsOpen ? "500px" : "0px" }}
        >
          <div className="px-10 pb-4 text-sm text-gray-700">
            <ul className="list-disc pl-5 space-y-1">
              <li>Chrome will open automatically with your application URL</li>
              <li>Perform the test case steps you want to automate</li>
              <li>We'll capture every action and generate the automation script</li>
              <li>You can save multiple test cases in one recording session</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
  
  {/* LEFT COLUMN */}
      <div className="flex flex-col ">
          
          {/* Test Case Name */}
          <div className="wizard-form-group">
            <label className="wizard-form-label">
              Test Case Name <span className="required">*</span>
            </label>
            <input
              type="text"
              className="wizard-form-input"
              value={testCaseName}
              onChange={(e) => {
                setTestCaseName(e.target.value);
                if (errors.testCaseName) {
                  setErrors(prev => ({ ...prev, testCaseName: '' }));
                }
              }}
              placeholder="e.g., Login Flow, User Registration"
              disabled={hasRecorded}
            />
            {errors.testCaseName && (
              <p className="wizard-form-error">{errors.testCaseName}</p>
            )}
            <p className="wizard-form-help">
              Enter a descriptive name for your test case (3-100 characters)
            </p>
          </div>

          {/* Priority */}
          <div className="wizard-form-group">
            <label className="wizard-form-label">
              Priority <span className="required">*</span>
            </label>
            <select
              className="wizard-form-input"
              value={priority}
              onChange={(e) => {
                setPriority(e.target.value);
                if (errors.priority) {
                  setErrors(prev => ({ ...prev, priority: '' }));
                }
              }}
              disabled={hasRecorded}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
            {errors.priority && (
              <p className="wizard-form-error">{errors.priority}</p>
            )}
            <p className="wizard-form-help">
              Sets the priority level for this test case
            </p>
          </div>

          {/* Application URL */}
          <div className="wizard-form-group">
            <label className="wizard-form-label flex items-center">
              <LuGlobe size={18} className="mr-2" />
              Application URL <span className="required ml-1">*</span>
            </label>
            <input
              type="url"
              className="wizard-form-input"
              value={projectUrl}
              onChange={(e) => {
                setProjectUrl(e.target.value);
                if (errors.projectUrl) {
                  setErrors(prev => ({ ...prev, projectUrl: '' }));
                }
              }}
              placeholder="https://your-application.com"
              disabled={hasRecorded}
            />
            {errors.projectUrl && (
              <p className="wizard-form-error">{errors.projectUrl}</p>
            )}
            <p className="wizard-form-help">
              The URL where your application is running
            </p>
          </div>

        </div>

        {/* RIGHT COLUMN */}
      <div className="wizard-form-group">
        <label className="wizard-form-label">
          Description
        </label>

        <textarea
          className="wizard-form-input min-h-[180px] h-[350px]"
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
            if (errors.description) {
              setErrors(prev => ({ ...prev, description: '' }));
            }
          }}
          placeholder="Add a description for this test case (optional)"
          disabled={hasRecorded}
        />

        {errors.description && (
          <p className="wizard-form-error">{errors.description}</p>
        )}

        <p className="wizard-form-help">
          Optional: Describe what this test case covers (max 500 characters)
        </p>
      </div>

      </div>

            {/* Recording Tips */}
       <div className="mb-8 border border-gray-200 bg-white rounded-xl">

      {/* Header */}
      <div className="flex items-center justify-between p-5 cursor-pointer" onClick={() => setIsRecordingTipsOpen(!isRecordingTipsOpen)}>
        <span className="font-semibold text-sm text-gray-800">
          💡 Recording Tips
        </span>
        <button >
          <LuChevronDown
            className={`transition-transform duration-200 ${
              isRecordingTipsOpen ? "rotate-180" : ""
            }`}
          />
        </button>
      </div>

  {/* Content */}
  <div
    className="overflow-hidden transition-all duration-300 ease-in-out"
    style={{ maxHeight: isRecordingTipsOpen ? "300px" : "0px" }}
  >
    <div className="px-5 pb-5 text-sm text-gray-700 space-y-3">
      <div className="flex gap-2">
        <span className="text-green-500 font-semibold">✓</span>
        <span>Keep your actions simple and focused - one clear user flow</span>
      </div>
      <div className="flex gap-2">
        <span className="text-green-500 font-semibold">✓</span>
        <span>Wait for pages to fully load before clicking</span>
      </div>
      <div className="flex gap-2">
        <span className="text-green-500 font-semibold">✓</span>
        <span>Use realistic test data (we'll help you parameterize it later)</span>
      </div>
      <div className="flex gap-2">
        <span className="text-green-500 font-semibold">✓</span>
        <span>Don't worry about mistakes - you can edit the script afterward</span>
      </div>
    </div>
  </div>

</div>

            {/* Example Scenarios */}
            <div style={{ marginTop: '1.5rem' }}>
              <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem', color: 'var(--text-secondary)' }}>
                Example scenarios to record:
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem', fontSize: '0.875rem' }}>
                <div style={{ padding: '0.75rem', backgroundColor: 'var(--card-bg)', borderRadius: '6px', border: '1px solid var(--border-color)' }}>
                  🔐 User login flow
                </div>
                <div style={{ padding: '0.75rem', backgroundColor: 'var(--card-bg)', borderRadius: '6px', border: '1px solid var(--border-color)' }}>
                  🛒 Add item to cart
                </div>
                <div style={{ padding: '0.75rem', backgroundColor: 'var(--card-bg)', borderRadius: '6px', border: '1px solid var(--border-color)' }}>
                  📝 Fill out a form
                </div>
                <div style={{ padding: '0.75rem', backgroundColor: 'var(--card-bg)', borderRadius: '6px', border: '1px solid var(--border-color)' }}>
                  🔍 Search and filter
                </div>
              </div>
            </div>
          </div>

          <WizardActions
            showBack={true}
            onBack={() => {
              previousStep();
              navigate('/app/onboarding?step=6&mode=recorder'); // Back to Agent+Extension step
            }}
            showNext={true}
            onNext={hasRecorded ? handleContinueToStep8 : handleStartRecording}
            nextLabel={
              hasRecorded ? (
                <>
                  Continue to Review
                </>
              ) : (
                <>
                  <LuPlay size={18} />
                  Record Test Case
                </>
              )
            }
            nextLoading={isStarting || isNavigating}
            nextDisabled={!testCaseName.trim() || !priority || !projectUrl.trim() || isStarting}
          />
        </WizardCard>
      </WizardContainer>
    </PrivateWizardLayout>
  );
};

export default Step8BRecordingSetup;